import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import L from "leaflet";

import { getEmissionWFSParams } from "src/utils/emission.utils";
import type { EmissionType } from "src/types";
import { useEmission } from "src/hooks/emissions";
import {} from "src/atoms/overlayers";
import { useWFSGetFeature } from "src/hooks";

interface Props {
  map: L.Map;
  type: EmissionType;
  params: {
    caseId: string;
    scenarioId: string;
    emissionId: string;
    segmentId?: string;
  };
}

export function EmissionOnMap(props: Props) {
  const { params } = props;
  const { data: emission } = useEmission(params.scenarioId, params.emissionId, props.type);

  return render();
  function render() {
    if (!emission) return null;
    return (
      <DataLoader params={params} type={emission.type} geoserverFilter={emission.geoserverFilter} map={props.map} />
    );
  }
}

export function DataLoader(props: Props & { type: EmissionType; geoserverFilter: number }) {
  const { type, geoserverFilter, map, params } = props;
  const wfsParams = getEmissionWFSParams(type, geoserverFilter);
  const { data: features } = useWFSGetFeature(wfsParams);
  const navigate = useNavigate();

  useEffect(() => {
    if (!features) return;

    const paneKey = "scenario-points";
    let pane = map.getPane(paneKey);
    if (!pane) pane = map.createPane(paneKey);
    pane.style.zIndex = "501";

    const blueSquareIcon = L.divIcon({
      className: "emission-point-icon",
      html: `
        <style>
          .emission-point-icon div {
            width: 16px;
            height: 16px;
            background-color: #40abff;
            border: 1px solid tomato;
            transition: background-color 0.2s ease, scale 0.2s ease;
            cursor: pointer;
          }
          .emission-point-icon:hover div {
            background-color: tomato;
          }
        </style>
        <div></div>
      `,
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    });

    const geosjson = L.geoJSON(features, {
      pointToLayer: (_, latlng) =>
        L.marker(latlng, {
          icon: blueSquareIcon,
          pane: paneKey,
        }),
      bubblingMouseEvents: true, // Enable bubbling for efficient event handling
    }).addTo(map);

    // Click event handler
    function handleClick(e: L.LeafletMouseEvent) {
      if ("feature" in e.sourceTarget) {
        const feature = e.sourceTarget.feature;

        if (feature.properties?.shape_id) {
          navigate(
            `/cases/${params.caseId}/scenarios/${params.scenarioId}/emissions/point-source/${params.emissionId}/point-sources/${feature.properties?.shape_id}`
          );
        }
      }
    }

    geosjson.on("click", handleClick);

    // cleanup
    return function cleanup() {
      geosjson.off("click", handleClick);
      geosjson.remove();
    };
  }, [features, map, navigate, params]);

  return null;
}
